// Sprint d028-workflow-agents (D2) — a `Workflow` launch's N inner agents,
// rendered from the persisted `workflow_progress` snapshot (D1).
// SubagentPanel-shaped (AI Elements ChainOfThought, ADR D-026), `max-w-none`
// (D-sap-1 — the panel fills its border width, NOT `max-w-live-activity`
// which is the LiveActivity label truncation width, challenger M8).
// Workflow-only: Agent-tool children carry no `workflow_progress` array and
// keep today's LiveActivity + SubagentPanel surfaces (§7 non-goal).
//
// Liveness (challenger B2 — the D013-EC-147 class): a row is LIVE iff
// `state !== 'done' && !settled && isPlausiblyLive(ctx)` — the ONE home of
// the plausibly-live half (`founder-layer.ts`), never re-derived from bare
// `state` or bare `!tr`. `settled` is the launch's OWN `tool_result` having
// landed (`!!tr`, computed once in `WorkflowPanel` and threaded down to
// every row) — spec D2 line 184: "Once the launch tool_result has landed
// (or the turn closed), every non-done row renders settled-unknown (no
// Loader)." `ctx.turnClosedAfter` covers the "or the turn closed" half;
// `settled` covers the "tool_result has landed" half — DROPPING either
// leaves a row spinning in the reachable window where the Workflow
// provably returned but the turn has not yet closed and the retained
// snapshot is still mid-run (§2/F9: `workflow_progress` is optional per
// event, so the last-seen snapshot can legitimately lag the launch's own
// resolution). Gate-2 (reviewer-lifecycle) blocker, fixed here: `isLive`
// used to read `!isDone && isPlausiblyLive(ctx)` alone, so the settled-via-
// tool_result half was silently dropped even though the HEADER (`:203`)
// already implemented it correctly — the per-agent rows, the whole point
// of this sprint, were LAXER than the summary line above them.
// `ctx` is built ONCE from the LAUNCH `tool_use` MESSAGE (`toolUseMessage`)
// and shared by every row AND the header (R2-B1/R3-M4): `ConversationList
// .tsx`'s `turnClosedAfterById` is keyed by MESSAGE id, populated ONLY for
// `tool_use` rows — a ctx built from the `workflow_progress` row's id or
// from `parentToolUseId` (a `tool_use_id`, not a message id) would read
// `turnClosedAfter=false` forever and degenerate to `agentWorking` alone,
// reintroducing EC-147 N-fold. Header liveness is `!tr &&
// isPlausiblyLive(ctx)` (D3) — `SubagentPanel.tsx:189`'s bare `!tr` is NOT
// the pattern to copy: a killed/interrupted/rate-limited Workflow never
// receives its launch `tool_result`, so a header keyed on bare `!tr` would
// spin forever in persisted history, on the one element always visible
// while the panel is collapsed.
//
// Selectors (R5-M2): panel root `workflow-panel`, the scope of every
// "no raw id" check (§8 item 6 — `agentId`/`taskId`/`tool_use_id` never
// render, founder-first-vision.md §8). Per-row disclosure
// `workflow-agent-row-toggle` / body `workflow-agent-row-body` — an
// unnamed affordance has no selector and a wrong one fails OPEN. Every row
// renders its toggle UNCONDITIONALLY (R7-B1) — a `state:'start'` row has
// no `resultPreview` but still shows its `promptPreview` body, never an
// empty one (D2's defining sentence, R8-M2); an ABSENT preview field
// renders the i18n `workflowPanel.noPreview` key, never nothing, never a
// raw field name (R9-M3). Each row owns its OWN `Collapsible` state,
// CLOSED by default (R9-M1) — the panel-scoped `ChainOfThoughtContent`
// (ONE shared `isOpen`) must not carry the row body.
//
// Copy is i18n, never a raw identifier (challenger M9, D013-EC-214): the
// header noun reuses the EXISTING `Workflow` humanisation
// (`verb-map.ts` / `headline.ts` `HARNESS_TOOL_COPY.Workflow`) via the new
// `workflowPanel.*` keys — the raw tool identifier `Workflow` never
// reaches founder copy.

import { useMemo, useState } from 'react';
import { WorkflowIcon, ChevronDownIcon } from 'lucide-react';
import type { Message, WorkflowAgent, WorkflowProgressContent } from '../types';
import { asToolResult, asToolUse } from '../lib/messages';
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
} from './ai-elements/chain-of-thought';
// DS.8 (D8) — `ui/collapsible.tsx` retired; the Radix primitive imported direct.
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible';
import { Loader } from './ai-elements/loader';
import { useTranslation } from '../i18n';
import { modelLabel } from '../lib/models';
import { isPlausiblyLive, type RowLivenessCtx } from '../lib/tool-rendering/founder-layer';

export interface WorkflowPanelProps {
  /** The same four props the dispatch already supplies to SubagentPanel
   * (ConversationList.tsx) — R4-m7. */
  ctxForMessage?: (m: Message) => RowLivenessCtx;
  // The launch tool_use message (the `Workflow` call itself).
  toolUseMessage: Message;
  // Full parent_tool_use_id → children map from groupBySubagent(). Used
  // ONLY to key the launch's own `workflow_progress` snapshot row
  // (R4-m7) — never for ctx.
  childrenByParent: Map<string, Message[]>;
  // The launch's own tool_result, if landed.
  resultMessage?: Message;
}

function isWorkflowProgressContent(c: unknown): c is WorkflowProgressContent {
  return (
    typeof c === 'object' && c !== null && Array.isArray((c as { agents?: unknown }).agents)
  );
}

// Pre-mortem #2 promised `Array.isArray` PLUS element-shape tolerance
// (spec §5: "absent, null, non-array, empty array -> no-op, no throw").
// The backend guard only validated the ARRAY itself — a single malformed
// ELEMENT (e.g. a CLI event carrying `workflow_progress: [null]`) passes
// `Array.isArray` and would throw on `agent.state` mid-render
// (`WorkflowAgentRow`), taking the whole conversation timeline down with
// it. Filtered at the panel boundary: a non-object element is dropped,
// never rendered, never crashes a sibling row.
function sanitizeAgents(agents: unknown[]): WorkflowAgent[] {
  return agents.filter((a): a is WorkflowAgent => typeof a === 'object' && a !== null);
}

// Founder-first §8 item 6 — `agentId`/`taskId`/`tool_use_id` never render.
// Absent a `label` (every MEASURED element carries one, §2/F9 — this is
// the fallback for the unmeasured edge), the 1-based position is used —
// NEVER the raw id (R6-B1: the fixture element lacking a label is the ONE
// configuration that would reveal an agentId-as-label leak, and the
// correct implementation must not have one).
function rowLabel(agent: WorkflowAgent): string | null {
  if (typeof agent.label === 'string' && agent.label.length > 0) return agent.label;
  if (typeof agent.index === 'number') return `#${agent.index + 1}`;
  return null;
}

function WorkflowAgentRow({
  agent,
  ctx,
  settled,
}: {
  agent: WorkflowAgent;
  ctx?: RowLivenessCtx;
  // The LAUNCH's own tool_result has landed (`!!tr` in WorkflowPanel) —
  // see the file-header comment for why this is a THIRD, independent
  // input to row liveness, never folded into `ctx`.
  settled: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isDone = agent.state === 'done';
  const isLive = !isDone && !settled && isPlausiblyLive(ctx);
  const statusLabel = isLive
    ? t('workflowPanel.running')
    : isDone
      ? t('workflowPanel.done')
      : t('workflowPanel.interrupted');
  const label = rowLabel(agent);
  const duration =
    typeof agent.durationMs === 'number' ? (agent.durationMs / 1000).toFixed(1) : null;
  const toolCalls = typeof agent.toolCalls === 'number' ? agent.toolCalls : null;
  // D2 — `resultPreview` once `done`, `promptPreview` before that; the
  // measured `start` shape carries `promptPreview` and NO `resultPreview`
  // (§2). Absent field -> `noPreview`, never an empty body.
  const previewRaw = isDone ? agent.resultPreview : agent.promptPreview;
  const body =
    typeof previewRaw === 'string' && previewRaw.length > 0
      ? previewRaw
      : t('workflowPanel.noPreview');
  // Joined, not interleaved-with-literal-separators: `label` is null for
  // the unmeasured no-label/no-index edge (rowLabel), and an unconditional
  // `' · '` ahead of `statusLabel` left a leading separator with nothing
  // before it in that case ("· (done)"). Filtering nulls before joining
  // means the separator only ever appears BETWEEN two real segments.
  const headerText = [
    label,
    agent.model ? modelLabel(agent.model) : null,
    statusLabel,
    duration !== null ? `${duration}s` : null,
    toolCalls !== null
      ? toolCalls === 1
        ? t('subagent.tools', { count: toolCalls })
        : t('subagent.toolsPlural', { count: toolCalls })
      : null,
  ]
    .filter((segment): segment is string => segment !== null)
    .join(' · ');

  return (
    <div className="pl-3 border-l border-border" data-testid="workflow-agent-row">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          data-testid="workflow-agent-row-toggle"
          className="flex w-full items-center gap-2 text-left text-body text-foreground transition-colors hover:text-foreground/80 focus:outline-none focus:ring-2 focus:ring-border"
        >
          <ChevronDownIcon
            className={`size-3.5 shrink-0 text-muted-foreground/70 transition-transform ${
              open ? 'rotate-180' : 'rotate-0'
            }`}
          />
          <span className="min-w-0 flex-1 truncate">{headerText}</span>
          {isLive && (
            <span
              className="inline-flex shrink-0 items-center"
              role="status"
              aria-label={t('workflowPanel.running')}
            >
              <Loader size={12} />
            </span>
          )}
        </CollapsibleTrigger>
        {/* Radix `CollapsibleContent` always renders its OWN wrapper div
            (hidden, empty) regardless of `open` — only the children passed
            to it are conditionally mounted (`isOpen && children` in
            `@radix-ui/react-collapsible`). The testid goes on this INNER
            div so `queryAllByTestId('workflow-agent-row-body')` is
            genuinely 0 while closed, mounted only once opened — the 0->N
            mount proof (R9-M1) would be vacuous against the outer
            wrapper. */}
        <CollapsibleContent>
          <div
            data-testid="workflow-agent-row-body"
            className="mt-1 pl-5 py-1 text-body whitespace-pre-wrap break-words text-foreground"
          >
            {body}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export function WorkflowPanel({
  toolUseMessage,
  childrenByParent,
  resultMessage,
  ctxForMessage,
}: WorkflowPanelProps) {
  const { t } = useTranslation();
  const tu = asToolUse(toolUseMessage);
  const tr = resultMessage ? asToolResult(resultMessage) : null;
  const isError = tr?.is_error === true;

  // R2-B1/R3-M4 — ctx built from the LAUNCH message, shared by header and
  // every row. NEVER from the workflow_progress row or parentToolUseId.
  const ctx = ctxForMessage?.(toolUseMessage);
  // D3 — header liveness mirrors the row rule, never bare `!tr`.
  const isRunning = !tr && isPlausiblyLive(ctx);

  const snapshot = useMemo(() => {
    const children = tu.tool_use_id ? childrenByParent.get(tu.tool_use_id) : undefined;
    const row = children?.find((m) => m.type === 'workflow_progress');
    if (!row) return null;
    return isWorkflowProgressContent(row.content) ? row.content : null;
  }, [childrenByParent, tu.tool_use_id]);

  const agents = sanitizeAgents(snapshot?.agents ?? []);

  // Derived from the launch tool_use/tool_result timestamps — orthogonal
  // to whether a workflow_progress snapshot has landed yet (D5: no
  // snapshot -> header-only, identical to today).
  const duration = useMemo(() => {
    if (!resultMessage) return null;
    const start = Date.parse(toolUseMessage.timestamp);
    const end = Date.parse(resultMessage.timestamp);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
    return Math.max(0, end - start);
  }, [toolUseMessage, resultMessage]);

  return (
    <div
      className={`border rounded-lg overflow-hidden text-body ${
        isError ? 'border-status-error/40 bg-status-error/10' : 'border-border bg-popover/60'
      }`}
      data-testid="workflow-panel"
      data-error={isError ? 'true' : 'false'}
      data-running={isRunning ? 'true' : 'false'}
    >
      {/* D-sap-1 — `max-w-none` overrides the primitive's `max-w-prose`
          cap at THIS call site only (twMerge: last wins), NOT
          `max-w-live-activity` (the LiveActivity label truncation width,
          challenger M8) — the panel fills its border width. */}
      <ChainOfThought defaultOpen={false} className="max-w-none">
        <ChainOfThoughtHeader
          icon={WorkflowIcon}
          data-testid="workflow-header"
          className={`px-3 py-2 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-border ${
            isError ? 'hover:bg-status-error/15' : ''
          }`}
        >
          <span
            className={`flex min-w-0 items-center gap-2 text-body ${
              isError ? 'text-status-error' : 'text-foreground'
            }`}
          >
            <span className="truncate">
              {t('workflowPanel.header')}
              {agents.length > 0 &&
                ` · ${
                  agents.length === 1
                    ? t('workflowPanel.agents', { count: agents.length })
                    : t('workflowPanel.agentsPlural', { count: agents.length })
                }`}
              {duration !== null && ` · ${(duration / 1000).toFixed(1)}s`}
              {isRunning && (
                <span
                  className="ml-2 inline-flex items-center gap-1 text-caption"
                  role="status"
                  aria-label={t('workflowPanel.running')}
                >
                  <Loader size={12} />
                  {t('workflowPanel.running')}
                </span>
              )}
              {isError && (
                <span className="ml-2 text-caption text-status-error">{t('subagent.error')}</span>
              )}
            </span>
          </span>
        </ChainOfThoughtHeader>
        <ChainOfThoughtContent
          className="mt-0 border-t border-border px-3 py-2 space-y-2"
          data-testid="workflow-body"
        >
          {agents.length > 0 && (
            <div className="space-y-1" data-testid="workflow-agent-list">
              {agents.map((agent, i) => (
                <WorkflowAgentRow
                  key={typeof agent.agentId === 'string' ? agent.agentId : `wf-agent-${i}`}
                  agent={agent}
                  ctx={ctx}
                  settled={!!tr}
                />
              ))}
            </div>
          )}
        </ChainOfThoughtContent>
      </ChainOfThought>
    </div>
  );
}
